import {
  AppShellRailTile,
  Box,
  Menu,
  MenuPopup,
  MenuTrigger,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";
import { useState } from "react";

import { WorkspaceAvatar } from "#app/components/brand/workspace-avatar";
import { WorkspaceMenuContent } from "#app/components/navigation/workspace-menu-content";
import { useWorkspace } from "#app/lib/data/workspace-context";

/**
 * The workspace mark at the head of the module rail — the desktop workspace
 * switcher. It is a rail tile like any other (same 32px chip, same hover), so
 * the workspace reads as the first item of the navigation stack rather than a
 * separate header widget. The mark alone carries it: no name, no caret, in
 * either rail mode, because an identity tile is never captioned.
 *
 * It opens the shared `WorkspaceMenuContent`, so switching, management and
 * create-or-join keep one implementation with the mobile selector.
 */
export function WorkspaceRailTile() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { activeWorkspace } = useWorkspace();
  const name = activeWorkspace?.name ?? "Workspace";

  return (
    <Menu onOpenChange={setMenuOpen} open={menuOpen}>
      <Tooltip>
        <TooltipTrigger
          render={
            <MenuTrigger
              render={
                <AppShellRailTile
                  // Icon-only in both rail modes — see the note above.
                  compact
                  label={`Switch workspace. Current workspace: ${name}`}
                  visual={
                    <WorkspaceAvatar
                      avatarColor={activeWorkspace?.avatarColor}
                      logo={activeWorkspace?.settings?.logo}
                      name={name}
                      // Fills the chip. An identity mark is always "on", so it
                      // wears the same filled 32px square an active module's
                      // chip does — where a module glyph sits at 16px inside an
                      // empty chip until it activates.
                      size="default"
                    />
                  }
                />
              }
            />
          }
        />
        {/* Two rows, because the mark alone does not say what KIND of thing it
          * is: a monogram tile reads as "some avatar" until the tooltip names
          * the role. The caption is the role, the second line is the value. */}
        <TooltipPopup side="right">
          <Box direction="column">
            <Text size="2xs" variant="muted">
              Your Workspace
            </Text>
            <Text size="xs" weight="medium">
              {name}
            </Text>
          </Box>
        </TooltipPopup>
      </Tooltip>
      {/* Drops from the tile it belongs to, its left edge on the tile's:
        * the workspace mark heads the rail, so the menu reads as that head
        * opening downward over the column rather than as a panel arriving
        * from somewhere else on screen. */}
      <MenuPopup align="start" padding="none" side="bottom" width="panel">
        <WorkspaceMenuContent onClose={() => setMenuOpen(false)} />
      </MenuPopup>
    </Menu>
  );
}
